import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, firstValueFrom, map, of } from 'rxjs';
import {
  AccessModules,
  CompanyResponse,
  MyUser,
  PermissionsForRequisitions,
  Token,
  TokenResponse,
  UserResponse
} from '../shared/models/user.model';
// import { baseURL } from './config';
import { environment } from '../../../environments/environment';
import { generateUUID, handleApiResponse } from '../utils/common-methods';
import { AlertMessageService } from './alert-message.service';
import { UserPermissionsService } from './user-permissions.service';
import {UserBackendAdapter, UserModel} from "../features/project-management/models/user.model";
import { AuthService, User } from '@auth0/auth0-angular';
import { Router } from '@angular/router';
import { collection, collectionChanges, collectionData, Firestore, onSnapshot} from '@angular/fire/firestore';



@Injectable({
  providedIn: 'root',
})
export class UserService {
  public headers = new HttpHeaders();
  private tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private auxUser = new UserBackendAdapter();
  private isOnMultipleCheck = false;

  private user: User | null | undefined;

  constructor(
    private http: HttpClient,
    private userPermissionsService: UserPermissionsService,
    private alertService: AlertMessageService,
    private authService: AuthService,
    private router: Router,
    private firestore: Firestore
  ) {
    this.tokenSubject.subscribe((token: string) => {
      this.headers = new HttpHeaders({ Authorization: 'Bearer ' + token });
    });

    this.authService.user$.subscribe((user) => {
      if(user) {
        this.user = user;
      }
    });
  }

  async onDetectMultipleAuthSession() {
    
    if(!this.isOnMultipleCheck) {
      const company = localStorage.getItem('companyName');
      const uuid = localStorage.getItem("uuid");
      const user = this.user ?? await firstValueFrom(this.authService.user$)

      const unsuscribeSession = onSnapshot(collection(this.firestore, `/dw/sessions/${company!.toLocaleLowerCase()}/${user!.email!.toLocaleLowerCase()}/active-sessions`), (querySnapshot) => {
        if(querySnapshot.empty) return;
        let destroy = true;
        for (const doc of querySnapshot.docs) {
          if(doc.id == uuid) {
            destroy = false;
            break;
          }
         
        }

        if(destroy) {
          unsuscribeSession();
          this.authService.logout();
          this.router.navigate(['/login']);
        }
      });
      this.isOnMultipleCheck = true;
    }
  }

  public getAccessModules(): Observable<AccessModules> {
    const apiURL = `${environment.baseURL}/users/my`;
    return this.http.get<UserResponse>(apiURL, { headers: this.headers }).pipe(
        map((response: UserResponse) => {
          return (handleApiResponse(response, this.alertService) as MyUser).user.accessModules as AccessModules ?? {};
        }),
        catchError((error) => {
          console.error('Error fetching user data:', error);
          this.alertService.updateAlert(error.error!);
          this.alertService.changeErrorState(true);
          return of({});
        })
    );
  }

  public getUserPermissionsInRequisitions(): Observable<void> {
    const apiURL = `${environment.baseURL}/requisition_users/permission`;
    return this.http.get<UserResponse>(apiURL, { headers: this.headers }).pipe(
      map((response: UserResponse) => {
        const res = handleApiResponse(response, this.alertService)
        this.userPermissionsService.setPermissions(res as PermissionsForRequisitions);
      })
    );
  }

  public getToken(token: any) {
    let uuid = localStorage.getItem("uuid");
    if(!uuid) {
      uuid = generateUUID();
      localStorage.setItem("uuid", generateUUID());
    }
    const user = this.user;
    const apiURL = `${environment.baseURL}/validate`;

    return this.http.post<TokenResponse>(apiURL, {
      ...token,
      uuid: uuid,
      email: user?.email ?? "",
    }, { headers: this.headers }).pipe(
      map((response: TokenResponse) => {
        const res = handleApiResponse(response, this.alertService);
        this.tokenSubject.next(res.token ?? '');

        if(res.token) {
          const expiresAt = Date.now() + ((res.expires_in ?? 3600) * 1000);
          localStorage.setItem('token_data', JSON.stringify({ expires_at: expiresAt }));
          this.onDetectMultipleAuthSession();
        }
        
        return res as Token;
      }),
      catchError((error) => {
        console.error('Error fetching user data:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of(null);
      })
    );
  }

  getDataUser(): Observable<MyUser> {
    const apiURL = `${environment.baseURL}/users/my`;
    return this.http.get<UserResponse>(apiURL, { headers: this.headers }).pipe(
      map((response: UserResponse) => {
        return handleApiResponse(response, this.alertService) as MyUser ?? {};
        })
    );
  }

  public validateCompany(company: string): Observable<boolean> {
    const apiURL = `${environment.baseURL}/company/validate/${company}`;
    return this.http.get<CompanyResponse>(apiURL, { headers: this.headers }).pipe(
      map((response: CompanyResponse) => {
        const res = handleApiResponse(response, this.alertService)
        if (!response.error['has-error']) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  public getUsers(context?: string, project?: number): Observable<UserModel[]> {
    let apiURL = `${environment.baseURL}/users`;
    if (context) {
      apiURL += `?context=${context}`;
    }
    if (project) {
      apiURL += `&project=${project}`;
    }

    return this.http.get<UserResponse>(apiURL, { headers: this.headers })
      .pipe(
        map((response: UserResponse) => {
          return (handleApiResponse(response, this.alertService) as any).map((u: any) => {
            return this.auxUser.adapt(u);
          })
        }),
        catchError((error) => {
          console.error('Error fetching user data:', error);
          this.alertService.updateAlert(error.error!);
          this.alertService.changeErrorState(true);
          return of([]);
        })
    );
  }
}
